import { Logger, LoggerService } from "@Shared/logger";
import { BLUECONIC_DEFAULT_TIMEOUT_MS } from "../_models/blueconic.constants";
import { BlueConicReadyOptions } from "../_models/blueconic.types";

export class BlueConicReadinessService {
  private static instance: BlueConicReadinessService;

  static get Instance(): BlueConicReadinessService {
    if ( this.instance === null || this.instance === undefined ) {
      this.instance = new BlueConicReadinessService();
    }
    return this.instance;
  }

  private readyPromise: Promise<boolean> | null = null;
  private readyResolved = false;
  private readyValue = false;
  private Logger: Logger = LoggerService.createLogger("BlueConicReadinessService");

  constructor() {
    this.Logger.trace("****  Blueconic Ready CTOR ****");
  }

  public blueConicReady( { timeout }: BlueConicReadyOptions = {} ): Promise<boolean> {
    const startTime = Date.now();

    if ( this.readyResolved && this.readyValue === true ) {
      this.Logger.trace(
        "BlueConicReadinessService | blueConicReady | returning cached value:",
        this.readyValue
      );
      return Promise.resolve( true );
    }

    if ( this.readyPromise ) {
      this.Logger.trace(
        "BlueConicReadinessService | blueConicReady | returning existing promise"
      );
      return this.readyPromise;
    }

    const maxWait = timeout ?? BLUECONIC_DEFAULT_TIMEOUT_MS;

    this.Logger.trace(
      "BlueConicReadinessService | blueConicReady | checking BC readiness"
    );

    this.readyPromise = new Promise<boolean>(( resolve ) => {
      let pollTimerId: ReturnType<typeof setTimeout> | undefined;
      let globalTimeoutId: ReturnType<typeof setTimeout> | undefined;

      const clearTimers = (): void => {
        if ( globalTimeoutId !== undefined ) {
          clearTimeout( globalTimeoutId );
          globalTimeoutId = undefined;
        }
        if ( pollTimerId !== undefined ) {
          clearTimeout( pollTimerId );
          pollTimerId = undefined;
        }
      };

      const finish = ( value: boolean ): void => {
        clearTimers();
        this.resolveReady( value, resolve );
      };

      globalTimeoutId = setTimeout( () => {
        if ( !this.readyResolved ) {
          this.Logger.trace(
            "BlueConicReadinessService | blueConicReady | timeout at:",
            ( Date.now() - startTime ) / 1000,
            "s"
          );
          finish( false );
        }
      }, maxWait );

      if ( window.blueConicClient?.profile ) {
        this.Logger.trace(
          "BlueConicReadinessService | blueConicReady | BC already ready"
        );
        finish( true );
        return;
      }

      const poll = (): void => {
        if ( this.readyResolved ) return;

        if ( window.blueConicClient?.event ) {
          this.Logger.trace(
            "BlueConicReadinessService | blueConicReady | BC client detected"
          );

          const unsubscribe = window.blueConicClient.event.subscribe(
            window.blueConicClient.event.onReady,
            {},
            () => {
              this.Logger.trace(
                "BlueConicReadinessService | blueConicReady | onReady fired at:",
                ( Date.now() - startTime ) / 1000,
                "s"
              );
              unsubscribe?.();
              finish( true );
            }
          );

          return;
        }

        pollTimerId = setTimeout( poll, 500 );
      };

      poll();
    } );

    return this.readyPromise;
  }

  private resolveReady( value: boolean, resolve: ( val: boolean ) => void ): void {
    if ( this.readyResolved ) return;

    this.Logger.trace(
      "BlueConicReadinessService | blueConicReady | resolved with:",
      value
    );
    this.readyResolved = true;
    this.readyValue = value;
    resolve( value );

    if ( value === false ) {
      this.readyPromise = null;
      this.readyResolved = false;
    }
  }
}
